import React from 'react';
import { shallowEqual } from 'react-redux';
import { FontIcon, mergeStyleSets } from '@fluentui/react';
import { releaseText, resolveThemeColors, Utilities } from '../../helpers';
import { tint } from '../../helpers/theme';
import { useAppSelector } from '../../store';
import { selectTranslate } from '../../store/selectors/ui';
import type { ReleaseEntry } from '../../helpers/release-notes';

type WhatsNewCalloutProps = {
    release: ReleaseEntry;
    onOpen: () => void;
};

export const WhatsNewCallout = ({ release, onOpen }: WhatsNewCalloutProps) => {
    const translate = useAppSelector(selectTranslate);
    const lang = useAppSelector((state) => state.uiReducer.lang);
    const colors = useAppSelector(resolveThemeColors, shallowEqual);

    const classes = mergeStyleSets({
        root: {
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            margin: '0 0 16px',
            padding: '10px 12px',
            border: 0,
            borderRadius: 4,
            backgroundColor: tint(colors.primary, 0.1),
            color: colors.appBgText,
            fontFamily: 'inherit',
            textAlign: 'left',
            cursor: 'pointer',
            selectors: {
                ':hover': { backgroundColor: tint(colors.primary, 0.16) },
                ':focus-visible': {
                    outline: `2px solid ${colors.icon}`,
                    outlineOffset: 2,
                },
            },
        },
        icon: { flex: '0 0 auto', fontSize: 16, color: colors.icon },
        text: { flex: '1 1 auto', minWidth: 0, margin: '0 10px 0 12px' },
        title: {
            display: 'block',
            fontSize: 14,
            lineHeight: '1.35',
            fontWeight: 600,
        },
        subtitle: {
            display: 'block',
            marginTop: 2,
            fontSize: 12,
            lineHeight: '1.4',
            opacity: 0.8,
        },
        chevron: { flex: '0 0 auto', fontSize: 12, color: colors.icon },
    });

    return (
        <button type="button" className={classes.root} onClick={onOpen}>
            <FontIcon iconName="Megaphone" className={classes.icon} />
            <span className={classes.text}>
                <span className={classes.title}>
                    {Utilities.formatString(
                        translate('whatsnew_settings_link'),
                        release.version,
                    )}
                </span>
                <span className={classes.subtitle}>
                    {releaseText(release.subtitle, lang)}
                </span>
            </span>
            <FontIcon iconName="ChevronRightMed" className={classes.chevron} />
        </button>
    );
};
